/**
 * Per-process visual tone for cart operation sections + chips.
 *
 * The tone follows the platform-managed operation-category colour
 * (``Process.operation_category_color`` — a ``#RRGGBB`` an admin edits in
 * "Systémové kategorie"), which is the same value that inks the process
 * icon in admin chrome and in the cart, so the two agree. Only a process
 * with no operation category falls back to the seeded Tailwind token
 * (``Process.color``), resolved through the palette table below; an
 * unknown token falls back to ``DEFAULT_TONE``.
 *
 * Colour values are the Tailwind palette hues for each family. Each
 * tone bundles:
 *   - ``bg``    — soft fill behind the section card + selected variants
 *   - ``ink``   — text + icon colour on the section header
 *   - ``border``— hairline accent on cards and chips
 *   - ``dot``   — saturated dot used in legends + checkbox accents
 *   - ``border_left`` — 3px ribbon on the left edge of the section card
 */

export interface OperationTone {
  bg: string;
  ink: string;
  border: string;
  dot: string;
  borderLeft: string;
}

const DEFAULT_TONE: OperationTone = {
  bg: "#f5f5f4",
  ink: "#44403c",
  border: "#d6d3d0",
  dot: "#78716c",
  borderLeft: "#a8a29e",
};

// One entry per Tailwind colour family (bg=50, ink=800, border=200,
// dot/borderLeft=600) — extend when a process starts using a new token.
const TONES: Record<string, OperationTone> = {
  slate: {
    bg: "#f8fafc",
    ink: "#1e293b",
    border: "#e2e8f0",
    dot: "#475569",
    borderLeft: "#475569",
  },
  red: {
    bg: "#fef2f2",
    ink: "#991b1b",
    border: "#fecaca",
    dot: "#dc2626",
    borderLeft: "#dc2626",
  },
  rose: {
    bg: "#fff1f2",
    ink: "#9f1239",
    border: "#fecdd3",
    dot: "#e11d48",
    borderLeft: "#e11d48",
  },
  orange: {
    bg: "#fff7ed",
    ink: "#9a3412",
    border: "#fed7aa",
    dot: "#ea580c",
    borderLeft: "#ea580c",
  },
  amber: {
    bg: "#fffbeb",
    ink: "#92400e",
    border: "#fde68a",
    dot: "#d97706",
    borderLeft: "#d97706",
  },
  green: {
    bg: "#f0fdf4",
    ink: "#166534",
    border: "#bbf7d0",
    dot: "#16a34a",
    borderLeft: "#16a34a",
  },
  emerald: {
    bg: "#ecfdf5",
    ink: "#065f46",
    border: "#a7f3d0",
    dot: "#059669",
    borderLeft: "#059669",
  },
  teal: {
    bg: "#f0fdfa",
    ink: "#115e59",
    border: "#99f6e4",
    dot: "#0d9488",
    borderLeft: "#0d9488",
  },
  sky: {
    bg: "#f0f9ff",
    ink: "#075985",
    border: "#bae6fd",
    dot: "#0284c7",
    borderLeft: "#0284c7",
  },
  blue: {
    bg: "#eff6ff",
    ink: "#1e40af",
    border: "#bfdbfe",
    dot: "#2563eb",
    borderLeft: "#2563eb",
  },
  indigo: {
    bg: "#eef2ff",
    ink: "#3730a3",
    border: "#c7d2fe",
    dot: "#4f46e5",
    borderLeft: "#4f46e5",
  },
  violet: {
    bg: "#f5f3ff",
    ink: "#5b21b6",
    border: "#ddd6fe",
    dot: "#7c3aed",
    borderLeft: "#7c3aed",
  },
  purple: {
    bg: "#faf5ff",
    ink: "#6b21a8",
    border: "#e9d5ff",
    dot: "#9333ea",
    borderLeft: "#9333ea",
  },
  pink: {
    bg: "#fdf2f8",
    ink: "#9d174d",
    border: "#fbcfe8",
    dot: "#db2777",
    borderLeft: "#db2777",
  },
};

const HEX_RE = /^#([0-9a-f]{6})$/i;

/** ``rgb`` mixed toward ``target`` by ``weight`` (0 = keep, 1 = target). */
function mix(
  rgb: [number, number, number],
  target: 0 | 255,
  weight: number,
): string {
  const channel = (c: number): string =>
    Math.round(c * (1 - weight) + target * weight)
      .toString(16)
      .padStart(2, "0");
  return `#${rgb.map(channel).join("")}`;
}

/**
 * Tone derived from an admin-authored category hex. The saturated hue
 * itself is the dot / ribbon; the fill and hairline are the hue washed
 * out toward white, and the ink is the hue darkened toward black — more
 * for a bright hue than a dark one, so the header text stays readable on
 * the pale fill whichever colour the admin picked.
 */
function toneFromHex(hex: string): OperationTone | null {
  const match = HEX_RE.exec(hex.trim());
  if (!match) return null;
  const value = match[1];
  const rgb: [number, number, number] = [
    parseInt(value.slice(0, 2), 16),
    parseInt(value.slice(2, 4), 16),
    parseInt(value.slice(4, 6), 16),
  ];
  // Rec. 709 luma, good enough to tell a bright hue from a dark one.
  const luma = (0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]) / 255;
  const saturated = `#${value.toLowerCase()}`;
  return {
    bg: mix(rgb, 255, 0.93),
    ink: mix(rgb, 0, 0.35 + 0.5 * luma),
    border: mix(rgb, 255, 0.7),
    dot: saturated,
    borderLeft: saturated,
  };
}

/**
 * Tone for a process — a ``#RRGGBB`` operation-category colour is
 * derived on the fly, a Tailwind token resolves through the palette
 * table. Unset / unrecognised falls back to the neutral tone.
 */
export function getOperationTone(color: string | null | undefined): OperationTone {
  if (!color) return DEFAULT_TONE;
  if (color.startsWith("#")) return toneFromHex(color) ?? DEFAULT_TONE;
  return TONES[color] ?? DEFAULT_TONE;
}

/**
 * The colour a process's cart tone is keyed by: the platform-managed
 * operation-category hex, falling back to the seeded ``color`` token for
 * processes that belong to no category. Single resolver so every cart
 * surface (section, part card, variant row, bulk bar, summary) tints
 * from the same source.
 */
export function operationToneColor(
  process:
    | { operation_category_color?: string | null; color?: string | null }
    | null
    | undefined,
): string | null {
  return process?.operation_category_color || process?.color || null;
}
